import * as moment from 'moment';

import {DATE_FORMAT} from '../../../app.constants';
import {FormValues} from '../../form-generator/form-generator.interfaces';
import {
  HrCalendarContractPeriod,
  HrCalendarWeek,
  HrCalendarWorkSchedule,
  HrCalendarWorkScheduleByDate,
  WorkScheduleWeekdayZone,
  WORK_SCHEDULE_GROUP_NAME
} from '../hr-calendar.models';

const WEEKDAY_ZONES_BY_DAY_OF_WEEK: WorkScheduleWeekdayZone[] = Object.values(WorkScheduleWeekdayZone);

export class HrCalendarWorkScheduleUtil {

  public static fromContractValues(values: FormValues, contract: HrCalendarContractPeriod): HrCalendarWorkSchedule[] {
    const contractBegin = this.toIsoDate(contract?.begindate);
    const contractEnd = this.toIsoDate(contract?.enddate);

    return (values?.get(WORK_SCHEDULE_GROUP_NAME) ?? [])
      .filter(block => !block.cleared)
      .map(block => ({
        block,
        hoursByWeekday: WEEKDAY_ZONES_BY_DAY_OF_WEEK.map(zone => this.toHours(block.values?.[zone]?.value))
      }))
      .filter(({hoursByWeekday}) => hoursByWeekday.some(hours => hours !== null))
      .map(({block, hoursByWeekday}) => ({
        beginDate: this.latest(this.toIsoDate(block.begindate), contractBegin),
        endDate: this.earliest(this.toIsoDate(block.enddate), contractEnd),
        hoursByWeekday
      }))
      .filter(schedule => !schedule.beginDate || !schedule.endDate || schedule.beginDate <= schedule.endDate);
  }

  public static resolveDays(weeks: HrCalendarWeek[], schedules: HrCalendarWorkSchedule[]): HrCalendarWorkScheduleByDate {
    const result: HrCalendarWorkScheduleByDate = {};
    if (!schedules?.length) {
      return result;
    }

    const latestFirst = [...schedules].sort((a, b) => (b.beginDate ?? '').localeCompare(a.beginDate ?? ''));

    weeks.forEach(week => week.days.forEach(day => {
      const schedule = latestFirst.find(candidate => this.covers(candidate, day.isoDate));
      if (!schedule) {
        return;
      }
      const hours = schedule.hoursByWeekday[day.dayOfWeek] ?? 0;
      result[day.isoDate] = hours > 0
        ? {isWorkingDay: true, hours}
        : {isWorkingDay: false, hours: null};
    }));
    return result;
  }

  private static covers(schedule: HrCalendarWorkSchedule, isoDate: string): boolean {
    return (!schedule.beginDate || schedule.beginDate <= isoDate) && (!schedule.endDate || isoDate <= schedule.endDate);
  }

  private static toHours(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const hours = Number(String(value).replace(',', '.'));
    return Number.isFinite(hours) ? hours : null;
  }

  private static toIsoDate(value: string): string | undefined {
    if (!value) {
      return undefined;
    }
    const date = moment(value, [DATE_FORMAT, moment.ISO_8601], true);
    return date.isValid() ? date.format(DATE_FORMAT) : undefined;
  }

  private static latest(first?: string, second?: string): string | undefined {
    if (!first || !second) {
      return first || second;
    }
    return first > second ? first : second;
  }

  private static earliest(first?: string, second?: string): string | undefined {
    if (!first || !second) {
      return first || second;
    }
    return first < second ? first : second;
  }
}
